import { createCrudApi } from '@core/api/createCrudApi';
import type { BaseEntity } from '@core/interfaces';
import axiosInstance from '@core/utils/axiosInstance';
import { VestmentCategory } from '../src/enum/vestmentCategory';
import { VestmentType } from '../src/enum/vestmentType';

export interface Design extends BaseEntity {
  code: string;
  vestmentType: VestmentType | null;
  v1VestmentType: VestmentType | null;
  imagePath?: string | null;
  thumbnailPath?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  centerFinish?: number | null;
  isHidden?: boolean;
}

export interface DesignFilters {
  category: VestmentCategory | '';
  variant: string;
}

export interface CreateDesignData {
  code: string;
  vestmentType: VestmentType;
  image?: File;
  centerFinish?: number | null;
}

export type UpdateDesignData = Partial<CreateDesignData>;

const designApi = createCrudApi<Design, CreateDesignData, UpdateDesignData>(
  'design'
);

export const getDesignById = designApi.getById;
export const deleteDesign = designApi.delete;

export interface DesignQuery {
  search?: string;
  category?: string;
  variant?: string;
  vestmentType?: VestmentType;
}

export interface DesignPageParams extends DesignQuery {
  offset: number;
  limit: number;
}

export interface DesignPage {
  items: Design[];
  total: number;
  offset: number;
  limit: number;
}

export const designKeys = {
  all: ['designs'] as const,
  pages: ['designs', 'page'] as const,
  page: (query?: DesignQuery) => ['designs', 'page', query] as const,
  pickers: ['designs', 'picker'] as const,
  picker: (query: DesignQuery) => ['designs', 'picker', query] as const,
};

export async function getDesignsPage(
  params: DesignPageParams
): Promise<DesignPage> {
  const { data } = await axiosInstance.get<DesignPage>('/design/page', {
    params,
  });
  return data;
}

function buildDesignFormData(data: UpdateDesignData) {
  const formData = new FormData();
  if (data.code !== undefined) {
    formData.append('code', data.code);
  }
  if (data.vestmentType !== undefined) {
    formData.append('vestmentType', data.vestmentType);
  }
  if (data.image) {
    formData.append('image', data.image);
  }
  if (data.centerFinish !== undefined && data.centerFinish !== null) {
    formData.append('centerFinish', String(data.centerFinish));
  }
  return formData;
}

export async function createDesign(data: CreateDesignData): Promise<Design> {
  const formData = buildDesignFormData(data);
  const response = await axiosInstance.post('/design', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function updateDesign(
  id: number,
  data: UpdateDesignData
): Promise<Design> {
  const formData = buildDesignFormData(data);
  const response = await axiosInstance.post(`/design/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}
